import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SentEmailModel } from '@app/contacts/shared/sent-email.model';
import { SentEmailService } from '@app/contacts/shared/sent-email.service';
import { ComposeEmailModalComponent } from '@app/communications/compose-email-modal.component';
import { ComposeContact } from '@app/communications/communication.model';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';

/**
 * TVG-313: the emails the system has sent to this contact, newest first.
 *
 * Written by the server whenever an email actually goes out, so staff see what
 * the contact has received without reading it as a staff note.
 *
 * TVG-343: the same list now also carries the emails staff write themselves,
 * and the panel gains a Send an email button that opens the Communications
 * compose box with this contact already chosen. The button and the status
 * column sit behind the `communications` toggle; with it off this is exactly
 * the read-only TVG-313 panel again.
 */
@Component({
  selector: 'app-contact-sent-emails',
  standalone: true,
  imports: [DatePipe, NgClass, ButtonComponent, ComposeEmailModalComponent],
  templateUrl: './contact-sent-emails.component.html',
})
export class ContactSentEmailsComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactId!: number;
  /** Needed by the compose box so it can address the email without a lookup. */
  @Input() contactName = '';
  @Input() contactEmail = '';
  @Input() contactEmailPermission = true;
  /**
   * TVG-360: a text needs somewhere to go and permission to go there. Both come
   * from the contact block above this panel.
   */
  @Input() contactPhoneNumbers: string[] = [];
  @Input() contactTextPermission = true;

  @ViewChild(ComposeEmailModalComponent) private composeModal?: ComposeEmailModalComponent;

  protected sentEmails: SentEmailModel[] = [];
  protected loading = false;
  protected loadError: string | null = null;
  protected currentPage = 1;
  protected lastPage = 1;
  protected total = 0;

  protected composeOpen = false;
  protected sentNotice: string | null = null;

  private readonly pageSize = 25;

  constructor(
    private sentEmailService: SentEmailService,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && !changes['contactId'].firstChange) {
      this.sentEmails = [];
      this.currentPage = 1;
      this.load();
    }
  }

  /** TVG-343: with the toggle off there is no Send an email button at all. */
  protected get communicationsEnabled(): boolean {
    return this.featureToggleService.isEnabled('communications');
  }

  protected get composeContact(): ComposeContact {
    return {
      id: this.contactId,
      name: this.contactName,
      email: this.contactEmail,
      venue: null,
      email_permission: this.contactEmailPermission,
    };
  }

  /**
   * TVG-360: whether this contact could be texted at all — they need a mobile
   * number on file and must have agreed to texts.
   */
  protected get canSendSms(): boolean {
    return this.contactTextPermission && this.contactPhoneNumbers.some((number) => !!number?.trim());
  }

  /**
   * TVG-360: whether the application can send a text at all. Nothing in this
   * codebase sends one today — there is no provider, channel or route — so the
   * button is present where the layout puts it but cannot be pressed. Connecting
   * a text provider is its own piece of work; this case is layout and wording.
   */
  protected get smsSendingAvailable(): boolean {
    return false;
  }

  protected get smsUnavailableReason(): string {
    if (!this.smsSendingAvailable) {
      return 'Text sending is not connected yet';
    }
    if (!this.contactTextPermission) {
      return 'This contact has not agreed to texts';
    }
    if (!this.canSendSms) {
      return 'This contact has no mobile number';
    }
    return 'Send a text message';
  }

  /** Placeholder until a text provider is connected; see smsSendingAvailable. */
  protected openSms(): void {
    return;
  }

  protected get hasMore(): boolean {
    return this.currentPage < this.lastPage;
  }

  protected openCompose(): void {
    this.sentNotice = null;
    this.composeOpen = true;
    this.composeModal?.reset();
  }

  protected closeCompose(): void {
    this.composeOpen = false;
  }

  /**
   * The message is already recorded as Queued, so reloading page one puts it at
   * the top of this list straight away rather than after the queue has run.
   */
  protected onSent(): void {
    this.composeOpen = false;
    this.sentNotice = 'Queued. It sends in the background, so you do not have to wait.';
    this.currentPage = 1;
    this.load();
  }

  /** TVG-342: re-read the list, e.g. after an extra address filed replies here. */
  reload(): void {
    this.currentPage = 1;
    this.load();
  }

  protected statusClasses(sentEmail: SentEmailModel): string {
    switch (sentEmail.status) {
      case 'queued':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      case 'failed':
      case 'bounced':
        return 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300';
      case 'received':
        return 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-white/70';
      default:
        return 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300';
    }
  }

  protected loadMore(): void {
    if (!this.hasMore || this.loading) {
      return;
    }

    this.load(this.currentPage + 1);
  }

  private load(pageNum = 1): void {
    if (!this.contactId) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.sentEmailService.listForContact(this.contactId, pageNum, this.pageSize).subscribe({
      next: (response) => {
        const rows = response.data ?? [];
        this.sentEmails = pageNum === 1 ? rows : [...this.sentEmails, ...rows];
        this.currentPage = response.meta?.current_page ?? pageNum;
        this.lastPage = response.meta?.last_page ?? pageNum;
        this.total = response.meta?.total ?? this.sentEmails.length;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = error?.error?.message || 'Failed to load sent emails';
      },
    });
  }
}
